import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Reveal } from "@/components/Reveal";
import {
  BUSINESS,
  INTEREST_OPTIONS,
  SMS_CONSENT_TEXT,
  EMAIL_CONSENT_TEXT,
  FORM_NOTE,
} from "@/lib/site";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inputBase =
  "w-full rounded-lg border border-white/10 bg-[#0a0d14] px-4 py-3 text-sm text-white placeholder-slate-500 outline-none transition-colors focus:border-amber-400/60 focus:ring-1 focus:ring-amber-400/40";

const Field = ({ label, required, children, htmlFor }) => (
  <div className="space-y-2">
    <label
      htmlFor={htmlFor}
      className="block text-xs font-medium uppercase tracking-wider text-slate-400"
    >
      {label}
      {required && <span className="text-amber-400"> *</span>}
    </label>
    {children}
  </div>
);

export default function ContactForm() {
  const navigate = useNavigate();
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    firstName: "",
    lastName: "",
    company: "",
    email: "",
    mobilePhone: "",
    interest: "",
    message: "",
  });
  const [smsConsent, setSmsConsent] = useState(false);
  const [emailConsent, setEmailConsent] = useState(false);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.firstName || !form.lastName || !form.email) {
      toast.error("Please fill in your first name, last name, and email.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/submissions`, {
        ...form,
        interest: form.interest || "Something else",
        smsConsent,
        emailConsent,
        smsConsentText: SMS_CONSENT_TEXT,
        emailConsentText: EMAIL_CONSENT_TEXT,
      });
      navigate("/thanks");
    } catch (err) {
      console.error(err);
      toast.error("Something went wrong. Please try again or email us directly.");
      setSubmitting(false);
    }
  };

  return (
    <section
      id="contact"
      data-testid="contact"
      className="relative py-24 lg:py-32 bg-[#0b0f17] border-t border-white/10 scroll-mt-20"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid gap-14 lg:grid-cols-12 lg:gap-16">
          <div className="lg:col-span-4">
            <Reveal>
              <div className="rsm-eyebrow mb-5">Contact</div>
              <h2 className="font-display text-3xl sm:text-4xl font-bold tracking-tight text-white leading-[1.1]">
                Let's build your demand layer.
              </h2>
              <p className="mt-5 text-base leading-relaxed text-slate-400">
                Tell us what you're trying to grow. We'll respond within one
                business day.
              </p>
              <div className="mt-8 space-y-4 text-sm text-slate-300">
                <a
                  href={`mailto:${BUSINESS.email}`}
                  className="block hover:text-amber-400 transition-colors"
                >
                  {BUSINESS.email}
                </a>
                <a
                  href={BUSINESS.phoneHref}
                  className="block hover:text-amber-400 transition-colors"
                >
                  {BUSINESS.phone}
                </a>
                <div className="text-slate-500">{BUSINESS.hours}</div>
                <div className="text-slate-500">{BUSINESS.physicalAddress}</div>
              </div>
            </Reveal>
          </div>

          <div className="lg:col-span-8">
            <Reveal delay={0.1}>
              <form
                data-testid="contact-form"
                onSubmit={handleSubmit}
                noValidate
                className="rounded-2xl border border-white/10 bg-[#121927] p-6 sm:p-9"
              >
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <Field label="First name" required htmlFor="firstName">
                    <input
                      id="firstName"
                      data-testid="input-first-name"
                      className={inputBase}
                      value={form.firstName}
                      onChange={update("firstName")}
                      autoComplete="given-name"
                      required
                    />
                  </Field>
                  <Field label="Last name" required htmlFor="lastName">
                    <input
                      id="lastName"
                      data-testid="input-last-name"
                      className={inputBase}
                      value={form.lastName}
                      onChange={update("lastName")}
                      autoComplete="family-name"
                      required
                    />
                  </Field>
                  <Field label="Company" htmlFor="company">
                    <input
                      id="company"
                      data-testid="input-company"
                      className={inputBase}
                      value={form.company}
                      onChange={update("company")}
                      autoComplete="organization"
                    />
                  </Field>
                  <Field label="Email" required htmlFor="email">
                    <input
                      id="email"
                      type="email"
                      data-testid="input-email"
                      className={inputBase}
                      value={form.email}
                      onChange={update("email")}
                      autoComplete="email"
                      required
                    />
                  </Field>
                  <Field label="Mobile phone number" htmlFor="mobilePhone">
                    <input
                      id="mobilePhone"
                      type="tel"
                      data-testid="input-mobile-phone"
                      className={inputBase}
                      value={form.mobilePhone}
                      onChange={update("mobilePhone")}
                      autoComplete="tel"
                    />
                  </Field>
                  <Field label="I'm interested in" htmlFor="interest">
                    <select
                      id="interest"
                      data-testid="select-interest"
                      className={inputBase}
                      value={form.interest}
                      onChange={update("interest")}
                    >
                      <option value="" disabled>
                        Select a service…
                      </option>
                      {INTEREST_OPTIONS.map((o) => (
                        <option key={o} value={o} className="bg-[#0a0d14]">
                          {o}
                        </option>
                      ))}
                    </select>
                  </Field>
                </div>

                <div className="mt-5">
                  <Field label="Message" htmlFor="message">
                    <textarea
                      id="message"
                      data-testid="input-message"
                      rows={4}
                      className={inputBase + " resize-y"}
                      value={form.message}
                      onChange={update("message")}
                      placeholder="What are you trying to grow?"
                    />
                  </Field>
                </div>

                {/* Consent checkboxes — two separate, both optional, both unchecked by default */}
                <div className="mt-8 space-y-5">
                  <label
                    htmlFor="smsConsent"
                    className="flex gap-3.5 cursor-pointer rounded-xl border border-white/10 bg-[#0a0d14] p-4"
                  >
                    <input
                      id="smsConsent"
                      type="checkbox"
                      data-testid="checkbox-sms-consent"
                      checked={smsConsent}
                      onChange={(e) => setSmsConsent(e.target.checked)}
                      className="mt-0.5 h-5 w-5 shrink-0 accent-amber-500 cursor-pointer"
                    />
                    <span className="text-[13px] leading-relaxed text-slate-300">
                      <span className="mb-1 inline-block rounded bg-white/5 px-2 py-0.5 text-[10px] font-mono uppercase tracking-wider text-slate-400">
                        Optional
                      </span>
                      <br />
                      Yes, text me. By checking this box, I agree to receive
                      recurring marketing and customer care text messages from
                      Rolling Stone Marketing at the mobile number provided
                      above, including messages sent by an automatic telephone
                      dialing system. Message frequency varies (approximately
                      2-6 messages per month). Message and data rates may apply.
                      Consent is not a condition of purchase. Reply STOP to opt
                      out or HELP for help. See our{" "}
                      <Link
                        to="/privacy"
                        data-testid="consent-privacy-link"
                        className="text-amber-400 underline underline-offset-2 hover:text-amber-300"
                      >
                        Privacy Policy
                      </Link>{" "}
                      and{" "}
                      <Link
                        to="/terms"
                        data-testid="consent-terms-link"
                        className="text-amber-400 underline underline-offset-2 hover:text-amber-300"
                      >
                        Terms &amp; Conditions
                      </Link>
                      .
                    </span>
                  </label>

                  <label
                    htmlFor="emailConsent"
                    className="flex gap-3.5 cursor-pointer rounded-xl border border-white/10 bg-[#0a0d14] p-4"
                  >
                    <input
                      id="emailConsent"
                      type="checkbox"
                      data-testid="checkbox-email-consent"
                      checked={emailConsent}
                      onChange={(e) => setEmailConsent(e.target.checked)}
                      className="mt-0.5 h-5 w-5 shrink-0 accent-amber-500 cursor-pointer"
                    />
                    <span className="text-[13px] leading-relaxed text-slate-300">
                      <span className="mb-1 inline-block rounded bg-white/5 px-2 py-0.5 text-[10px] font-mono uppercase tracking-wider text-slate-400">
                        Optional
                      </span>
                      <br />
                      Email me too. I agree to receive marketing emails from
                      Rolling Stone Marketing. I can unsubscribe at any time
                      using the link in any email.
                    </span>
                  </label>
                </div>

                <p
                  data-testid="form-note"
                  className="mt-6 text-xs leading-relaxed text-slate-500"
                >
                  {FORM_NOTE}
                </p>

                <button
                  type="submit"
                  data-testid="submit-button"
                  disabled={submitting}
                  className="mt-8 inline-flex w-full sm:w-auto items-center justify-center rounded-full bg-amber-500 px-8 py-3.5 text-sm font-semibold text-[#0a0d14] transition-colors hover:bg-amber-400 disabled:opacity-60"
                >
                  {submitting ? "Sending…" : "Send message"}
                </button>
              </form>
            </Reveal>
          </div>
        </div>
      </div>
    </section>
  );
}
